/**
 * 互动小说后台作品管理的 API 出口：http 从 `./http.js` 取（basePath `/api/novel/admin`），
 * 响应经 `@drama/contracts/novel` 的 parse 函数校验；每个方法的 routeId 与服务端路由注释一致。
 */
import {
  parseNovelAdminPackDetail,
  parseNovelAdminPackList,
  parseNovelAdminPackResponse,
  parseNovelReleaseRecord,
  parseNovelReleaseResult,
  parseNovelReleaseState,
  parseNovelVersionExport,
} from '@drama/contracts/novel'

import { novelAdminHttp } from './http.js'


/**
 * q 由服务端按标题、slug、创作者邮箱模糊匹配。
 * @callchain.routeId novel.admin.packs.list
 * @param {{ page?: number, page_size?: number, status?: string, q?: string }} [query]
 */
export async function listNovelPacks(query = {}) {
  const response = await novelAdminHttp().get('/packs', { query, routeId: 'novel.admin.packs.list' })
  return parseNovelAdminPackList(response)
}

/**
 * @callchain.routeId novel.admin.packs.detail
 * @param {string} packId
 */
export async function getNovelPack(packId) {
  const response = await novelAdminHttp().get(`/packs/${encodeURIComponent(packId)}`, { routeId: 'novel.admin.packs.detail' })
  return parseNovelAdminPackDetail(response)
}

export async function getPublishedNovelVersion(packId) {
  const response = await novelAdminHttp().get(`/packs/${encodeURIComponent(packId)}/published-version/export`, { routeId: 'novel.admin.packs.publishedVersionExport' })
  return parseNovelVersionExport(response)
}

export async function uploadNovelOpeningCgAsset(packId, kind, file) {
  const data_url = await new Promise((resolve, reject) => {
    const reader = new globalThis.FileReader()
    reader.onerror = () => reject(new Error('request_failed'))
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(file)
  })
  return novelAdminHttp().post(`/packs/${encodeURIComponent(packId)}/opening-cg/assets`, {
    body: { kind, data_url }, routeId: 'novel.admin.packs.openingCgAssetUpload',
  })
}

/**
 * @callchain.routeId novel.admin.packs.publish
 * @param {string} packId
 */
export async function publishNovelPack(packId, reviewId) {
  const response = await novelAdminHttp().post(`/packs/${encodeURIComponent(packId)}/publish`, { body: { review_id: reviewId }, routeId: 'novel.admin.packs.publish' })
  return parseNovelAdminPackResponse(response)
}

/**
 * @callchain.routeId novel.admin.packs.unpublish
 * @param {string} packId
 */
export async function unpublishNovelPack(packId) {
  const response = await novelAdminHttp().post(`/packs/${encodeURIComponent(packId)}/unpublish`, { routeId: 'novel.admin.packs.unpublish' })
  return parseNovelAdminPackResponse(response)
}

/**
 * @callchain.routeId novel.admin.packs.featured
 * @param {string} packId
 * @param {{ featured_rank: number|null }} body
 */
export async function setNovelPackFeatured(packId, body) {
  const response = await novelAdminHttp().put(`/packs/${encodeURIComponent(packId)}/featured`, { body, routeId: 'novel.admin.packs.featured' })
  return parseNovelAdminPackResponse(response)
}

/**
 * 发布到正式环境区（C25）：连接状态、Main 当前版本与这部作品的发布记录。
 * @callchain.routeId novel.admin.packs.release
 * @param {string} packId
 */
export async function getNovelPackRelease(packId) {
  const response = await novelAdminHttp().get(`/packs/${encodeURIComponent(packId)}/release`, { routeId: 'novel.admin.packs.release' })
  return parseNovelReleaseState(response)
}

/**
 * 把选定版本发布到正式环境（仅 admin）。
 * @callchain.routeId novel.admin.packs.releaseToProduction
 * @param {string} packId
 * @param {string} versionId
 */
export async function releaseNovelPack(packId, versionId) {
  const response = await novelAdminHttp().post(`/packs/${encodeURIComponent(packId)}/release`, {
    body: { version_id: versionId },
    routeId: 'novel.admin.packs.releaseToProduction',
  })
  return parseNovelReleaseResult(response)
}

/**
 * 收尾卡住的发布记录（仅 admin，R-4）：只收超过传输超时没推进的进行中记录，置失败后放开这部作品的下一次发布。
 * @callchain.routeId novel.admin.packs.releaseFinish
 * @param {string} packId
 * @param {string} releaseId
 */
export async function finishNovelRelease(packId, releaseId) {
  const response = await novelAdminHttp().post(`/packs/${encodeURIComponent(packId)}/release/records/${encodeURIComponent(releaseId)}/finish`, {
    routeId: 'novel.admin.packs.releaseFinish',
  })
  return parseNovelReleaseRecord(response)
}
